import {
  MdAdd,
  MdCheck,
  MdDelete,
  MdDeleteOutline,
  MdOutlinePriorityHigh,
  MdOutlineViewList,
} from "react-icons/md";
import clsx from "clsx";
import InfoBlock from "../../components/InfoBlock/InfoBlock";
import { useTranslation } from "react-i18next";
import { ICON_SIZES } from "../../helpers/constants";
import useDarkMode from "../../hooks/useDarkMode";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import useMobile from "../../hooks/useMobile";
import useCollapsibles, {
  ETransactionPageMobileCollapsibles,
} from "../../hooks/useCollapsibles";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import CommonTable from "src/components/Tables/CommonTable";
import { useEffect, useMemo, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { formatCurrency } from "src/helpers/utils";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import CustomSelect from "src/components/Select/CustomSelect";
import useAllTransactions from "src/hooks/useAllTransactions";
import { ETransactionsTableKeys } from "src/components/Tables/tableIds";

export type TransactionDetailRowType = {
  quantity: string;
  description: string;
  unitPrice: string;
};

export type TransactionDetailRowsType = Array<TransactionDetailRowType>;

const NEW_DETAIL_ROW_TEMPLATE: TransactionDetailRowType = {
  quantity: "1",
  description: "",
  unitPrice: "",
};

const EMPTY_DESCRIPTION = "Agrega una descripción";

type TransactionDetailsType = {
  details: TransactionDetailRowsType;
  onDetailsChange: (data: TransactionDetailRowsType) => void;
};

const TransactionDetails: React.FC<TransactionDetailsType> = ({
  details = [],
  onDetailsChange,
}) => {
  const { currencySymbol } = useCurrentAccount();
  const { isPinnedColumn, updatePinnedColumns } = usePinnedColumns();
  const { allDescriptionsObject } = useAllTransactions();
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isTransactionPageMobileCollapsibleOpen,
    toggleTransactionPageMobileCollapsible,
  } = useCollapsibles();

  const isDetailedTransaction: boolean = !!details?.length;
  const [tableDetails, setTableDetails] = useState<any[]>([]);

  const onAddRow = () => {
    onDetailsChange([...tableDetails, NEW_DETAIL_ROW_TEMPLATE]);
  };

  useEffect(() => {
    setTableDetails(
      details?.map?.(
        (
          { quantity, description, unitPrice }: TransactionDetailRowType,
          index: number
        ) => {
          return {
            fakeIndex: index,
            quantity: Number(quantity),
            description,
            unitPrice: Number(unitPrice),
            subtotal: Number(quantity) * Number(unitPrice),
          };
        }
      )
    );
  }, [details]);

  const { isEnabled: isDarkMode } = useDarkMode();

  const formatLineNumber = (line: number) => {
    if (line < 10) {
      return `#0${line}`;
    }

    return `#${line}`;
  };

  const columns = useMemo(() => {
    return [
      {
        headerName: "#",
        sortable: false,
        width: 75,
        cellClass: clsx(["font-medium", "dark:text-cyan-400", "text-gray-500"]),
        pinned: isPinnedColumn("transactionDetailsLineNumberPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsLineNumberPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: any) => {
          return (
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "gap-1",
                "flex-shrink-0",
              ])}
            >
              <span>{formatLineNumber(params?.data?.fakeIndex + 1)}</span>
              {!params?.data?.description ||
              params?.data?.description === EMPTY_DESCRIPTION ||
              !Number(params?.data?.quantity) ? (
                <MdOutlinePriorityHigh
                  size={ICON_SIZES.XS}
                  className={clsx([
                    "text-red-500",
                    "dark:text-rose-400/80",
                    "shrink-0",
                  ])}
                />
              ) : (
                <MdCheck
                  size={ICON_SIZES.XS}
                  className={clsx(["text-lime-400", "shrink-0"])}
                />
              )}
            </div>
          );
        },
      },
      {
        pinned: isPinnedColumn("transactionDetailsDescriptionPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsDescriptionPinned: isPinned,
              });
            },
          },
        },
        field: "description",
        headerName: t("table_headings.description"),
        flex: 1,
        minWidth: 200,
        cellEditor: (params: ICellRendererParams) => {
          return (
            <CustomSelect
              options={allDescriptionsObject}
              value={params?.value}
              onChange={(newValue: any) => {
                const selectedText =
                  typeof newValue === "string"
                    ? newValue
                    : newValue?.label || newValue?.value || "";

                params.data.description = selectedText;
                params.data.subtotal =
                  Number(params.data.quantity) * Number(params.data.unitPrice);

                params.api.refreshCells({
                  rowNodes: [params.node],
                  force: true,
                });

                const rowData: TransactionDetailRowsType = [];
                params.api.forEachNode((node: any) => {
                  const { quantity, description, unitPrice } = node.data;
                  rowData.push({
                    quantity: String(quantity),
                    description,
                    unitPrice: String(unitPrice),
                  });
                });
                onDetailsChange(rowData);
              }}
              allowSearchModal
            />
          );
        },

        editable: true,
        sortable: false,
        valueFormatter: (params: any) =>
          params.value ? params.value : EMPTY_DESCRIPTION,
        onCellValueChanged: (params: any) => {},
        valueSetter: (params: any) => {
          const value = params.newValue;
          params.data.description = value === EMPTY_DESCRIPTION ? "" : value;
          return true;
        },
        cellRenderer: (params: any) => {
          return (
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "gap-1",
                "flex-shrink-0",
              ])}
            >
              <span
                className={clsx([
                  {
                    "text-red-400 dark:text-purple-400": !String(
                      params?.data?.description || ""
                    ).trim(),
                  },
                ])}
              >
                {String(params?.data?.description || "").trim() ||
                  EMPTY_DESCRIPTION}
              </span>
            </div>
          );
        },
      },
      {
        field: "quantity",
        pinned: isPinnedColumn("transactionDetailsQuantityPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsQuantityPinned: isPinned,
              });
            },
          },
        },
        headerName: t("table_headings.quantity"),
        minWidth: 80,
        resizable: true,
        editable: true,
        cellEditor: "agNumberCellEditor",
        sortable: false,
        cellEditorParams: {
          min: 0,
        },

        valueFormatter: (params: ICellRendererParams) =>
          formatCurrency({ currency: "", value: params?.data?.quantity }),
        onCellValueChanged: (params: any) => {},
      },
      {
        field: "unitPrice",
        pinned: isPinnedColumn("transactionDetailsUnitPricePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsUnitPricePinned: isPinned,
              });
            },
          },
        },
        headerName: t("table_headings.unit_price"),
        sortable: false,
        minWidth: 120,
        resizable: true,
        editable: true,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: 0,
        },

        valueFormatter: (params: ICellRendererParams) =>
          formatCurrency({ currency: currencySymbol, value: params?.value }),
        onCellValueChanged: (params: any) => {},
      },
      {
        field: "subtotal",
        pinned: isPinnedColumn("transactionDetailsSubtotalPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsSubtotalPinned: isPinned,
              });
            },
          },
        },
        headerName: t("table_headings.subtotal"),
        sortable: false,
        minWidth: 100,
        valueFormatter: (params: ICellRendererParams) =>
          formatCurrency({ currency: currencySymbol, value: params?.value }),
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        pinned: isPinnedColumn("transactionDetailsActionsPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                transactionDetailsActionsPinned: isPinned,
              });
            },
          },
        },
        headerName: t("table_headings.actions"),
        sortable: false,
        width: 80,
        cellRendererParams: (params: any) => {
          return {
            buttons: [
              {
                icon: <MdDeleteOutline size={ICON_SIZES.SM} />,
                onClick: () => {
                  const { data } = params;
                  const indexToDelete = params?.data?.fakeIndex;
                  const rowData: any = [];

                  params?.api?.forEachNode((node: any) => {
                    const { quantity, description, unitPrice, fakeIndex } =
                      node?.data;
                    if (fakeIndex !== indexToDelete) {
                      rowData.push({ quantity, description, unitPrice });
                    }
                  });

                  onDetailsChange(rowData);
                },
                className: clsx(["grid-button"]),
              },
            ],
          };
        },
      },
    ];
  }, [isPinnedColumn]);

  const hasEmptyDescriptions = useMemo(() => {
    return !!tableDetails?.some(
      (detail: any) => !detail?.description?.trim?.()
    );
  }, [tableDetails]);

  return (
    <CollapsibleBlock
      title={t("transaction_page.collapsibles.details.title")}
      badge={
        isDetailedTransaction
          ? hasEmptyDescriptions
            ? `${t("transaction_page.collapsibles.details.rows", {
                count: details?.length,
              })}`
            : t("transaction_page.collapsibles.details.rows", {
                count: details?.length,
              })
          : t("transaction_page.collapsibles.details.empty_rows")
      }
      badgeKind={hasEmptyDescriptions ? "danger" : "info"}
      uppercase
      open={
        isMobile
          ? isTransactionPageMobileCollapsibleOpen(
              ETransactionPageMobileCollapsibles.DETAILS
            )
          : collapsiblePreferences.transactionDetailsOpen
      }
      onChange={(isOpen: boolean) => {
        if (isMobile) {
          toggleTransactionPageMobileCollapsible(
            ETransactionPageMobileCollapsibles.DETAILS
          );
        } else {
          updateCollapsible({ transactionDetailsOpen: isOpen });
        }
      }}
    >
      {isDetailedTransaction ? (
        <div
          className={clsx([
            "flex",
            "flex-col",
            "gap-4",
            "w-full",
            "rounded-md",
            "overflow-auto",
          ])}
        >
          <CommonTable
            onEditStopped={(data: any) => {
              const rowData: any = [];
              data?.api?.forEachNode((node: any) => {
                const { quantity, description, unitPrice } = node?.data;
                rowData.push({ quantity, description, unitPrice });
              });

              onDetailsChange(rowData);
            }}
            onSelectionChange={() => {}}
            data={tableDetails}
            columns={columns}
            singleSelection
            settingsKey={ETransactionsTableKeys.TRANSACTION_DETAILS}
          />
          <InfoBlock
            title={t("transaction_page.collapsibles.details.add_more_rows")}
            buttons={[
              {
                type: "button",
                kind: EButtonKind.NORMAL,
                text: isMobile
                  ? ""
                  : t("transaction_page.collapsibles.details.buttons.add"),
                tooltip: isMobile
                  ? t("transaction_page.collapsibles.details.buttons.add")
                  : "",
                onClick: () => onAddRow(),
                icon: <MdAdd size={32} />,
              },
              {
                tooltip: t(
                  "transaction_page.collapsibles.details.buttons.clear"
                ),
                text: isMobile
                  ? ""
                  : t("transaction_page.collapsibles.details.buttons.clear"),
                onClick: () => {
                  onDetailsChange([]);
                },
                icon: <MdDelete size={ICON_SIZES.MD} />,
                kind: EButtonKind.DANGER,
              },
            ]}
            message={t(
              "transaction_page.collapsibles.details.add_more_rows_message"
            )}
            displayMobileIcon
            icon={<MdOutlineViewList size={ICON_SIZES.MD} />}
          />
        </div>
      ) : (
        <InfoBlock
          title={t("transaction_page.collapsibles.details.message_title")}
          buttons={[
            {
              type: "button",
              kind: EButtonKind.NORMAL,
              text: isMobile
                ? ""
                : t("transaction_page.collapsibles.details.buttons.add"),
              tooltip: isMobile
                ? t("transaction_page.collapsibles.details.buttons.add")
                : "",
              onClick: () => onAddRow(),
              icon: <MdAdd size={32} />,
            },
          ]}
          message={t(
            "transaction_page.collapsibles.details.message_description"
          )}
          displayMobileIcon
          icon={<MdOutlineViewList size={ICON_SIZES.MD} />}
        />
      )}
    </CollapsibleBlock>
  );
};

export default TransactionDetails;
